import { useEffect, useRef } from 'react'

import { type GtLabel, SPEAKER_HOTKEYS } from './gtModel'

export interface GtHotkeyHandlers {
  onOk: () => void
  onFix: (hotkey: string) => void
  onLabel: (label: GtLabel) => void
  onDel: () => void
  onClear: () => void
  onEditText: () => void
  onSplit: () => void
  onAddSpeaker: () => void
  /** G: edit a speaker or group selected speakers into a room. */
  onRoom: () => void
  onNote: () => void
  onRename: () => void
  onUndo: () => void
  onRedo: () => void
  onNavigate: (direction: 1 | -1) => void
  onPlayToggle: () => void
  /** Tapped ←/→: jump to the previous or next review stop. */
  onJump: (direction: 1 | -1) => void
  /** Held ←/→: scrub through the active turn; released on keyup or window blur. */
  onScrub: (direction: 1 | -1) => void
  onScrubEnd: () => void
  onRestart: () => void
  onSave: () => void
  onShortcuts: () => void
}

export interface UseGtHotkeysArgs {
  handlers: GtHotkeyHandlers
  readOnly: boolean
  dialogOpen: boolean
  enabled: boolean
}

const NON_TEXT_INPUT_TYPES = new Set(['button', 'submit', 'reset', 'checkbox', 'radio'])

/** Only targets that consume keystrokes themselves suppress hotkeys; a focused button does not. */
const isSuppressedTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) return false
  if (target instanceof HTMLInputElement) return !NON_TEXT_INPUT_TYPES.has(target.type)
  if (target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement) return true
  return target.isContentEditable || target.closest('[contenteditable], [role="textbox"], [role="slider"]') != null
}

const ACTIVATION_KEYS = new Set(['Enter', 'NumpadEnter', 'Space'])

/** A focused button or link owns Enter/Space natively; handling them here too would double-fire. */
const isNativeActivation = (event: KeyboardEvent): boolean =>
  ACTIVATION_KEYS.has(event.code) &&
  event.target instanceof HTMLElement &&
  event.target.closest('button, a[href], [role="button"]') != null

const ALWAYS_ALLOWED = new Set([
  'Space',
  'KeyR',
  'Slash',
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'KeyJ',
  'KeyK',
])

const SCRUB_KEYS = new Set(['ArrowLeft', 'ArrowRight'])
export const ARROW_HOLD_MS = 180

/** Single window keydown listener; suppression order: composition, target, dialog, read-only. */
export const useGtHotkeys = ({ handlers, readOnly, dialogOpen, enabled }: UseGtHotkeysArgs): void => {
  const handlersRef = useRef(handlers)
  handlersRef.current = handlers

  useEffect(() => {
    if (!enabled) return

    let arrow: { code: string; direction: 1 | -1; timer: number | null; scrubbing: boolean } | null = null
    const clearArrow = () => {
      if (arrow?.timer != null) window.clearTimeout(arrow.timer)
      arrow = null
    }
    const endArrow = () => {
      const wasScrubbing = arrow?.scrubbing ?? false
      clearArrow()
      if (wasScrubbing) handlersRef.current.onScrubEnd()
    }
    const startArrow = (code: string) => {
      const current = {
        code,
        direction: (code === 'ArrowRight' ? 1 : -1) as 1 | -1,
        timer: null as number | null,
        scrubbing: false,
      }
      arrow = current
      current.timer = window.setTimeout(() => {
        if (arrow !== current) return
        current.timer = null
        current.scrubbing = true
        handlersRef.current.onScrub(current.direction)
      }, ARROW_HOLD_MS)
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.isComposing) return
      if (isSuppressedTarget(event.target)) return
      if (isNativeActivation(event)) return
      if (dialogOpen) return
      if (event.altKey) return

      const cmd = event.metaKey || event.ctrlKey
      if (event.repeat && !cmd) return

      const handlers = handlersRef.current

      if (cmd && !event.shiftKey && event.code === 'KeyS') {
        event.preventDefault()
        handlers.onSave()
        return
      }
      if (cmd && event.shiftKey && event.code === 'KeyZ') {
        event.preventDefault()
        handlers.onRedo()
        return
      }
      if (cmd && event.code === 'KeyZ') {
        event.preventDefault()
        handlers.onUndo()
        return
      }
      if (cmd) return

      if (readOnly && !ALWAYS_ALLOWED.has(event.code)) return

      switch (event.code) {
        case 'Enter':
        case 'NumpadEnter':
          event.preventDefault()
          handlers.onOk()
          return
        case 'KeyD':
          event.preventDefault()
          handlers.onDel()
          return
        case 'Backspace':
        case 'Delete':
          event.preventDefault()
          handlers.onClear()
          return
        case 'KeyE':
          event.preventDefault()
          handlers.onEditText()
          return
        case 'KeyS':
          event.preventDefault()
          handlers.onSplit()
          return
        case 'KeyN':
          event.preventDefault()
          handlers.onNote()
          return
        case 'KeyA':
          event.preventDefault()
          handlers.onAddSpeaker()
          return
        case 'KeyG':
          event.preventDefault()
          handlers.onRoom()
          return
        case 'KeyZ':
          event.preventDefault()
          if (event.shiftKey) handlers.onRedo()
          else handlers.onUndo()
          return
        case 'F2':
          event.preventDefault()
          handlers.onRename()
          return
        case 'ArrowUp':
        case 'KeyK':
          event.preventDefault()
          handlers.onNavigate(-1)
          return
        case 'ArrowDown':
        case 'KeyJ':
          event.preventDefault()
          handlers.onNavigate(1)
          return
        case 'Space':
          event.preventDefault()
          handlers.onPlayToggle()
          return
        case 'ArrowLeft':
        case 'ArrowRight':
          if (event.shiftKey) return
          event.preventDefault()
          if (arrow) {
            if (arrow.code !== event.code) {
              endArrow()
              startArrow(event.code)
            }
            return
          }
          startArrow(event.code)
          return
        case 'KeyU':
          event.preventDefault()
          handlers.onLabel('unknown')
          return
        case 'KeyX':
          event.preventDefault()
          handlers.onLabel('echo')
          return
        case 'KeyR':
          event.preventDefault()
          handlers.onRestart()
          return
        case 'Slash':
          if (event.shiftKey) {
            event.preventDefault()
            handlers.onShortcuts()
          }
          return
        default: {
          const digitMatch = /^Digit([1-9])$/.exec(event.code)
          if (digitMatch) {
            event.preventDefault()
            handlers.onFix(digitMatch[1])
            return
          }
          const letterMatch = /^Key([A-Z])$/.exec(event.code)
          if (letterMatch && SPEAKER_HOTKEYS.includes(letterMatch[1])) {
            event.preventDefault()
            handlers.onFix(letterMatch[1])
          }
        }
      }
    }

    const onKeyUp = (event: KeyboardEvent) => {
      if (!SCRUB_KEYS.has(event.code)) return
      if (!arrow) {
        handlersRef.current.onScrubEnd()
        return
      }
      if (arrow.code !== event.code) return
      const wasScrubbing = arrow.scrubbing
      const direction = arrow.direction
      clearArrow()
      if (wasScrubbing) handlersRef.current.onScrubEnd()
      else handlersRef.current.onJump(direction)
    }
    const onBlur = endArrow

    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('keyup', onKeyUp)
    window.addEventListener('blur', onBlur)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('keyup', onKeyUp)
      window.removeEventListener('blur', onBlur)
      endArrow()
    }
  }, [readOnly, dialogOpen, enabled])
}
